import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { DateInput } from '../../shared/DateInput';
import { formatDate } from '../../shared/formatting';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'),
    background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
});
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type ScheduleRow = {
    id: number;
    company_id: number | null;
    department_id: number | null;
    name: string;
    rotation_pattern_json: string | null;
    company_name: string | null;
    department_name: string | null;
    assignment_count: number;
};

type EmployeeRow = {
    id: number;
    first_name: string | null;
    last_name: string | null;
};

type CheckinRow = {
    id: number;
    employee_id: number;
    department_id: number | null;
    checkin_at: string;
    checkout_at: string | null;
    location: string | null;
    status: string;
    first_name: string | null;
    last_name: string | null;
    department_name: string | null;
};

type AssignmentRow = {
    id: number;
    employee_id: number;
    schedule_id: number;
    department_id: number | null;
    start_date: string | null;
    end_date: string | null;
    first_name: string | null;
    last_name: string | null;
};

const timeOf = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '—';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function ScheduleTab({ token, locale, user }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type Sub = 'schedules' | 'attendance';
    const [sub, setSub] = useState<Sub>('schedules');

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button type="button" style={tabBtn(sub === 'schedules')} onClick={() => setSub('schedules')}>{t('Rasporedi', 'Schedules')}</button>
                <button type="button" style={tabBtn(sub === 'attendance')} onClick={() => setSub('attendance')}>{t('Evidencija', 'Attendance')}</button>
            </div>
            {sub === 'schedules' ? <SchedulesPanel token={token} locale={locale} /> : <AttendancePanel token={token} locale={locale} isManager={user.role === 'superadministrator' || user.hr_access_level === 'hr_manager'} />}
        </div>
    );
}

// ── Rasporedi ───────────────────────────────────────────────────────────────
function SchedulesPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<ScheduleRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [shiftsRaw, setShiftsRaw] = useState('');
    const [creating, setCreating] = useState(false);
    const [activeId, setActiveId] = useState<number | null>(null);

    const load = useCallback(() => {
        setRows(null);
        setErr(null);
        api<{ schedules: ScheduleRow[] }>('/hr/schedules', token)
            .then((r) => setRows(r.schedules))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token]);
    useEffect(load, [load]);

    const create = async () => {
        setErr(null);
        if (!name.trim()) { setErr(t('Naziv je obavezan.', 'Name is required.')); return; }
        let rotation: unknown = undefined;
        if (shiftsRaw.trim()) {
            try { rotation = JSON.parse(shiftsRaw); }
            catch { setErr(t('Smjene moraju biti valjan JSON.', 'Shifts must be valid JSON.')); return; }
        }
        setCreating(true);
        try {
            await mutate('/hr/schedules', token, 'POST', { name: name.trim(), rotation_pattern_json: rotation });
            setName(''); setShiftsRaw('');
            load();
        } catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setCreating(false); }
    };

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Novi raspored', 'New schedule')}</strong>
                <div style={{ display: 'grid', gap: 10, marginTop: 12, maxWidth: 560 }}>
                    <label style={{ fontSize: '0.8rem', color: BROWN }}>
                        {t('Naziv', 'Name')}
                        <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('npr. Smjena A — 2 ujutro / 2 popodne', 'e.g. Shift A — 2 morning / 2 afternoon')} />
                    </label>
                    <label style={{ fontSize: '0.8rem', color: BROWN }}>
                        {t('Smjene (JSON, opcionalno)', 'Shifts (JSON, optional)')}
                        <textarea style={{ ...inp, minHeight: 90, fontFamily: 'monospace', fontSize: '0.78rem' }} value={shiftsRaw} onChange={(e) => setShiftsRaw(e.target.value)} placeholder={'[{"day":"Mon","start":"08:00","end":"16:00"}]'} />
                    </label>
                    {err ? <div style={{ color: '#b14040', fontSize: '0.82rem' }}>⚠ {err}</div> : null}
                    <div>
                        <button type="button" style={tabBtn(true)} disabled={creating} onClick={create}>
                            {creating ? t('Spremam…', 'Saving…') : '＋ ' + t('Kreiraj raspored', 'Create schedule')}
                        </button>
                    </div>
                </div>
            </div>

            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Rasporedi', 'Schedules')} {rows ? `(${rows.length})` : ''}</strong>
                {rows === null ? (
                    <div className="muted" style={{ marginTop: 12 }}>{t('Učitavam…', 'Loading…')}</div>
                ) : rows.length === 0 ? (
                    <div className="muted" style={{ marginTop: 12 }}>{t('Još nema rasporeda.', 'No schedules yet.')}</div>
                ) : (
                    <div style={{ overflowX: 'auto', marginTop: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Naziv', 'Name')}</th>
                                <th style={th}>{t('Firma', 'Company')}</th>
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={th}>{t('Dodjele', 'Assignments')}</th>
                                <th style={th}></th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td style={td}>{r.name}</td>
                                        <td style={td}>{r.company_name ?? '—'}</td>
                                        <td style={td}>{r.department_name ?? '—'}</td>
                                        <td style={td}>{r.assignment_count}</td>
                                        <td style={td}>
                                            <button type="button" style={{ ...tabBtn(activeId === r.id) }} onClick={() => setActiveId(activeId === r.id ? null : r.id)}>
                                                {t('Dodijeli', 'Assign')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {activeId !== null ? <AssignPanel token={token} locale={locale} scheduleId={activeId} onDone={load} /> : null}
        </div>
    );
}

// ── Dodjela zaposlenika rasporedu ────────────────────────────────────────────
function AssignPanel({ token, locale, scheduleId, onDone }: { token: string; locale: Locale; scheduleId: number; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [employees, setEmployees] = useState<EmployeeRow[] | null>(null);
    const [empSearch, setEmpSearch] = useState('');
    const [selected, setSelected] = useState<number[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [assignments, setAssignments] = useState<AssignmentRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api<{ employees: EmployeeRow[] }>('/hr/employees', token)
            .then((r) => setEmployees(r.employees))
            .catch(() => setEmployees([]));
    }, [token]);

    const toggle = (id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const submit = async () => {
        setErr(null);
        if (selected.length === 0) { setErr(t('Odaberi barem jednog zaposlenika.', 'Select at least one employee.')); return; }
        setBusy(true);
        try {
            const r = await mutate<{ assignments: AssignmentRow[] }>(`/hr/schedules/${scheduleId}/assign`, token, 'POST', {
                employee_ids: selected,
                start_date: startDate || null,
                end_date: endDate || null,
            });
            setAssignments(r.assignments);
            setSelected([]);
            onDone();
        } catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setBusy(false); }
    };

    return (
        <div style={{ ...card, borderColor: GOLD }}>
            <strong style={{ color: BROWN }}>{t('Dodjela rasporeda', 'Assign schedule')} #{scheduleId}</strong>
            <div style={{ display: 'grid', gap: 12, marginTop: 12, gridTemplateColumns: '1fr 1fr', maxWidth: 480 }}>
                <label style={{ fontSize: '0.8rem', color: BROWN }}>
                    {t('Od', 'From')}
                    <DateInput value={startDate} onChange={setStartDate} />
                </label>
                <label style={{ fontSize: '0.8rem', color: BROWN }}>
                    {t('Do', 'To')}
                    <DateInput value={endDate} onChange={setEndDate} />
                </label>
            </div>
            <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.8rem', color: BROWN, marginBottom: 6 }}>{t('Zaposlenici', 'Employees')}</div>
                {employees === null ? (
                    <div className="muted">{t('Učitavam…', 'Loading…')}</div>
                ) : employees.length === 0 ? (
                    <div className="muted">{t('Nema dostupnih zaposlenika.', 'No available employees.')}</div>
                ) : (
                    <>
                        <input style={{ ...inp, maxWidth: 320, marginBottom: 8 }} placeholder={t('Pretraži zaposlenika…', 'Search employee…')} value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                            {employees.filter((e) => `${e.first_name ?? ''} ${e.last_name ?? ''}`.toLowerCase().includes(empSearch.toLowerCase())).map((e) => (
                                <button key={e.id} type="button" style={tabBtn(selected.includes(e.id))} onClick={() => toggle(e.id)}>
                                    {[e.first_name, e.last_name].filter(Boolean).join(' ') || `#${e.id}`}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
            {err ? <div style={{ color: '#b14040', fontSize: '0.82rem', marginTop: 10 }}>⚠ {err}</div> : null}
            <div style={{ marginTop: 12 }}>
                <button type="button" style={tabBtn(true)} disabled={busy} onClick={submit}>
                    {busy ? t('Spremam…', 'Saving…') : t('Dodijeli', 'Assign')}
                </button>
            </div>
            {assignments && assignments.length > 0 ? (
                <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: '0.78rem', color: BROWN, marginBottom: 6 }}>{t('Trenutne dodjele', 'Current assignments')}</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                            <th style={th}>{t('Zaposlenik', 'Employee')}</th>
                            <th style={th}>{t('Od', 'From')}</th>
                            <th style={th}>{t('Do', 'To')}</th>
                        </tr></thead>
                        <tbody>
                            {assignments.map((a) => (
                                <tr key={a.id}>
                                    <td style={td}>{[a.first_name, a.last_name].filter(Boolean).join(' ') || `#${a.employee_id}`}</td>
                                    <td style={td}>{a.start_date ? formatDate(a.start_date, locale) : '—'}</td>
                                    <td style={td}>{a.end_date ? formatDate(a.end_date, locale) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}

// ── Evidencija (check-in/out) ────────────────────────────────────────────────
function AttendancePanel({ token, locale, isManager }: { token: string; locale: Locale; isManager: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<CheckinRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [editing, setEditing] = useState<CheckinRow | null>(null);
    const [showAudit, setShowAudit] = useState(false);

    const load = useCallback(() => {
        setRows(null);
        setErr(null);
        const qs: string[] = [];
        if (from) qs.push(`from=${from}`);
        if (to) qs.push(`to=${to}`);
        const path = '/hr/checkins' + (qs.length ? `?${qs.join('&')}` : '');
        api<{ checkins: CheckinRow[] }>(path, token)
            .then((r) => setRows(r.checkins))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token, from, to]);
    useEffect(load, [load]);

    return (
        <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                <strong style={{ color: BROWN }}>{t('Evidencija dolaska/odlaska', 'Attendance log')} {rows ? `(${rows.length})` : ''}</strong>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Od', 'From')}<div style={{ width: 150 }}><DateInput value={from} onChange={setFrom} /></div></label>
                    <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Do', 'To')}<div style={{ width: 150 }}><DateInput value={to} onChange={setTo} /></div></label>
                    {isManager ? (
                        <button
                            type="button"
                            onClick={() => setShowAudit((v) => !v)}
                            style={{ border: `1px solid ${GOLD}`, background: showAudit ? GOLD : '#fff', color: showAudit ? '#fff' : BROWN, borderRadius: 7, padding: '7px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                            {showAudit ? t('Sakrij log', 'Hide log') : t('📋 Log izmjena', '📋 Change log')}
                        </button>
                    ) : null}
                </div>
            </div>
            {isManager && showAudit ? <CheckinAuditView token={token} locale={locale} /> : null}
            {err ? <div style={{ color: '#b14040', fontSize: '0.82rem', marginBottom: 10 }}>⚠ {err}</div> : null}
            {rows === null ? (
                <div className="muted">{t('Učitavam…', 'Loading…')}</div>
            ) : rows.length === 0 ? (
                <div className="muted">{t('Nema zapisa evidencije.', 'No attendance records.')}</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                            <th style={th}>{t('Zaposlenik', 'Employee')}</th>
                            <th style={th}>{t('Poslovnica', 'Branch')}</th>
                            <th style={th}>{t('Datum', 'Date')}</th>
                            <th style={th}>{t('Dolazak', 'In')}</th>
                            <th style={th}>{t('Odlazak', 'Out')}</th>
                            <th style={th}>{t('Status', 'Status')}</th>
                            {isManager ? <th style={{ ...th, textAlign: 'right' }}>{t('Akcije', 'Actions')}</th> : null}
                        </tr></thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td style={td}>{[r.first_name, r.last_name].filter(Boolean).join(' ') || `#${r.employee_id}`}</td>
                                    <td style={td}>{r.department_name ?? '—'}</td>
                                    <td style={td}>{formatDate(r.checkin_at.slice(0, 10), locale)}</td>
                                    <td style={td}>{timeOf(r.checkin_at)}</td>
                                    <td style={td}>{timeOf(r.checkout_at)}</td>
                                    <td style={td}>
                                        <span style={{ color: r.status === 'late' ? '#b14040' : '#3f7a3f', fontWeight: 600, fontSize: '0.78rem' }}>
                                            {r.status === 'late' ? t('kasni / van rasporeda', 'late / off-schedule') : t('uredno', 'valid')}
                                        </span>
                                    </td>
                                    {isManager ? (
                                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <button type="button" onClick={() => setEditing(r)} title={t('Uredi', 'Edit')}
                                                style={{ border: `1px solid ${GOLD}`, background: '#fff', color: BROWN, borderRadius: 6, padding: '4px 9px', fontSize: '0.74rem', cursor: 'pointer', marginRight: 6 }}>
                                                {t('Uredi', 'Edit')}
                                            </button>
                                            <button type="button" onClick={() => removeRow(r)} title={t('Obriši', 'Delete')}
                                                style={{ border: '1px solid #d8b0b0', background: '#fff', color: '#b14040', borderRadius: 6, padding: '4px 9px', fontSize: '0.74rem', cursor: 'pointer' }}>
                                                {t('Obriši', 'Delete')}
                                            </button>
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {editing ? (
                <CheckinEditModal
                    token={token}
                    locale={locale}
                    row={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            ) : null}
        </div>
    );

    function removeRow(r: CheckinRow): void {
        const who = [r.first_name, r.last_name].filter(Boolean).join(' ') || `#${r.employee_id}`;
        const note = window.prompt(t(`Obrisati zapis evidencije za ${who}? (soft delete — ostaje u logu 180 dana)\nRazlog (opcionalno):`, `Delete attendance record for ${who}? (soft delete — kept in log 180 days)\nReason (optional):`), '');
        if (note === null) return;
        mutate(`/hr/checkins/${r.id}`, token, 'DELETE', { note })
            .then(() => load())
            .catch((e) => setErr(String(e?.message ?? e)));
    }
}

const hhmm = (iso: string | null): string => (iso && iso.length >= 16 ? iso.slice(11, 16) : '');

// ── Uredi unos evidencije (HR manager) ───────────────────────────────────────
function CheckinEditModal({ token, locale, row, onClose, onSaved }: {
    token: string; locale: Locale; row: CheckinRow; onClose: () => void; onSaved: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [inDate, setInDate] = useState(row.checkin_at.slice(0, 10));
    const [inTime, setInTime] = useState(hhmm(row.checkin_at) || '08:00');
    const [outDate, setOutDate] = useState(row.checkout_at ? row.checkout_at.slice(0, 10) : '');
    const [outTime, setOutTime] = useState(hhmm(row.checkout_at));
    const [status, setStatus] = useState(row.status === 'late' ? 'late' : 'valid');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const who = [row.first_name, row.last_name].filter(Boolean).join(' ') || `#${row.employee_id}`;

    const save = () => {
        if (!inDate) { setErr(t('Datum dolaska je obavezan.', 'Check-in date is required.')); return; }
        setBusy(true);
        setErr(null);
        const checkin_at = `${inDate} ${(inTime || '00:00')}:00`;
        const checkout_at = outDate ? `${outDate} ${(outTime || '00:00')}:00` : null;
        mutate(`/hr/checkins/${row.id}`, token, 'PATCH', { checkin_at, checkout_at, status, note })
            .then(() => onSaved())
            .catch((e) => { setErr(String(e?.message ?? e)); setBusy(false); });
    };

    const lbl: React.CSSProperties = { fontSize: '0.76rem', color: BROWN, fontWeight: 600, display: 'block', marginBottom: 4 };
    const timeInp: React.CSSProperties = { width: 90, padding: '7px 8px', border: '1px solid #d8cdb2', borderRadius: 7, fontSize: '0.85rem' };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(40,30,10,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80, padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 22, width: 'min(440px, 96vw)', boxShadow: '0 18px 50px rgba(0,0,0,0.25)' }}>
                <div style={{ fontWeight: 700, color: BROWN, fontSize: '1rem', marginBottom: 2 }}>{t('Uredi evidenciju', 'Edit attendance')}</div>
                <div className="muted" style={{ fontSize: '0.8rem', marginBottom: 14 }}>{who}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end', marginBottom: 12 }}>
                    <label><span style={lbl}>{t('Datum dolaska', 'Check-in date')}</span><DateInput value={inDate} onChange={setInDate} /></label>
                    <label><span style={lbl}>{t('Vrijeme', 'Time')}</span><input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} style={timeInp} /></label>
                    <label><span style={lbl}>{t('Datum odlaska', 'Check-out date')}</span><DateInput value={outDate} onChange={setOutDate} /></label>
                    <label><span style={lbl}>{t('Vrijeme', 'Time')}</span><input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} style={timeInp} /></label>
                </div>
                {outDate ? null : <div className="muted" style={{ fontSize: '0.74rem', marginTop: -4, marginBottom: 10 }}>{t('Prazan datum odlaska = nije odjavljen.', 'Empty check-out date = not checked out.')}</div>}
                <label style={{ display: 'block', marginBottom: 12 }}>
                    <span style={lbl}>{t('Status', 'Status')}</span>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: '100%', padding: '7px 8px', border: '1px solid #d8cdb2', borderRadius: 7, fontSize: '0.85rem' }}>
                        <option value="valid">{t('uredno', 'valid')}</option>
                        <option value="late">{t('kasni / van rasporeda', 'late / off-schedule')}</option>
                    </select>
                </label>
                <label style={{ display: 'block', marginBottom: 14 }}>
                    <span style={lbl}>{t('Razlog izmjene (u log)', 'Change reason (logged)')}</span>
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('npr. ispravak zaboravljene odjave', 'e.g. forgotten check-out fix')}
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #d8cdb2', borderRadius: 7, fontSize: '0.85rem' }} />
                </label>
                {err ? <div style={{ color: '#b14040', fontSize: '0.8rem', marginBottom: 10 }}>⚠ {err}</div> : null}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button type="button" onClick={onClose} style={{ border: '1px solid #d8cdb2', background: '#fff', color: BROWN, borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={save} style={{ border: 'none', background: GOLD, color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}</button>
                </div>
            </div>
        </div>
    );
}

// ── Log izmjena evidencije (180 dana) ────────────────────────────────────────
type AuditRow = {
    id: number; checkin_id: number | null; employee_id: number | null; action: string;
    actor_name: string | null; changes: string | { old?: Record<string, unknown>; new?: Record<string, unknown> } | null;
    note: string | null; created_at: string | null; first_name: string | null; last_name: string | null;
};

function CheckinAuditView({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<AuditRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        api<{ audit: AuditRow[] }>('/hr/checkins/audit', token)
            .then((r) => setRows(r.audit))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token]);

    const fmtChanges = (c: AuditRow['changes']): string => {
        if (!c) return '—';
        let obj: { old?: Record<string, unknown>; new?: Record<string, unknown> };
        try { obj = typeof c === 'string' ? JSON.parse(c) : c; } catch { return '—'; }
        const o = obj.old ?? {}; const n = obj.new ?? {};
        const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]));
        const parts = keys
            .filter((k) => String(o[k] ?? '') !== String(n[k] ?? ''))
            .map((k) => `${k}: ${o[k] ?? '∅'} → ${n[k] ?? '∅'}`);
        return parts.length ? parts.join('; ') : t('(bez promjene polja)', '(no field change)');
    };

    const actionLabel = (a: string): string =>
        a === 'delete' ? t('brisanje', 'delete') : a === 'restore' ? t('vraćanje', 'restore') : t('izmjena', 'edit');

    return (
        <div style={{ ...card, background: '#fdf8ec', marginBottom: 14 }}>
            <strong style={{ color: BROWN, fontSize: '0.9rem' }}>{t('Log izmjena evidencije', 'Attendance change log')} <span className="muted" style={{ fontWeight: 400, fontSize: '0.78rem' }}>· {t('čuva se 180 dana', 'kept 180 days')}</span></strong>
            {err ? <div style={{ color: '#b14040', fontSize: '0.8rem', marginTop: 8 }}>⚠ {err}</div> : null}
            {rows === null ? (
                <div className="muted" style={{ marginTop: 8 }}>{t('Učitavam…', 'Loading…')}</div>
            ) : rows.length === 0 ? (
                <div className="muted" style={{ marginTop: 8 }}>{t('Nema zabilježenih izmjena.', 'No recorded changes.')}</div>
            ) : (
                <div style={{ overflowX: 'auto', marginTop: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                            <th style={th}>{t('Kada', 'When')}</th>
                            <th style={th}>{t('Radnja', 'Action')}</th>
                            <th style={th}>{t('Zaposlenik', 'Employee')}</th>
                            <th style={th}>{t('Promjena', 'Change')}</th>
                            <th style={th}>{t('Razlog', 'Reason')}</th>
                            <th style={th}>{t('Tko', 'By')}</th>
                        </tr></thead>
                        <tbody>
                            {rows.map((a) => (
                                <tr key={a.id}>
                                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '0.76rem' }}>{a.created_at ? `${formatDate(a.created_at.slice(0, 10), locale)} ${a.created_at.slice(11, 16)}` : '—'}</td>
                                    <td style={td}><span style={{ color: a.action === 'delete' ? '#b14040' : BROWN, fontWeight: 600, fontSize: '0.76rem' }}>{actionLabel(a.action)}</span></td>
                                    <td style={td}>{[a.first_name, a.last_name].filter(Boolean).join(' ') || (a.employee_id ? `#${a.employee_id}` : '—')}</td>
                                    <td style={{ ...td, fontSize: '0.76rem' }}>{fmtChanges(a.changes)}</td>
                                    <td style={{ ...td, fontSize: '0.76rem' }}>{a.note || '—'}</td>
                                    <td style={{ ...td, fontSize: '0.76rem' }}>{a.actor_name || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
